import type { Session as LocksSdkSession } from '@synonymdev/locks-sdk';
import { ValidationErrorCode } from '@/libs/error/error.codes';
import { Err } from '@/libs/error/error.factories';
import { ErrorService } from '@/libs/error/error.types';
import { isAppError, isNotFound, isValidationError, toAppError } from '@/libs/error/error.utils';
import { stripPubkyPrefix } from '@/libs/utils/utils';
import { CompositeIdDomain } from '@/models/models.types';
import { buildCompositeIdFromPubkyUri } from '@/models/models.utils';
import { GuardedContentParser, LockContentParser, LockProofBundler } from '@/pipes/locks/locks.parser';
import { HomeserverService } from '@/services/homeserver/homeserver';
import { LocksService } from '@/services/locks/locks';
import type {
  LockFile,
  ReplicatedPost,
  TCreateContentLockResult,
  TExchangeSessionCodeParams,
  TFetchLockFileParams,
  TGenerateConnectUrlParams,
  TGeneratePaykitSetupUrlParams,
  TGuardedResource,
  TLocksSessionResult,
  TPaykitConnectionState,
  TPaykitSetupStatus,
  TRegisterGuardedResourceResult,
  TUnlockedAttachment,
  TUnlockedContent,
  TUnlockedListItem,
  TVerificationStatus,
} from '@/services/locks/locks.types';
import { VerifierType } from '@/services/locks/locks.types';
import type {
  TCreateLockContentParams,
  TFetchOwnContentParams,
  TFetchReplicatedAttachmentsParams,
  TFetchReplicatedContentParams,
  TFetchUnlockedContentParams,
  TFetchUnlockedListParams,
  TLockContentFile,
  TPaymentBundleParams,
  TPaymentLockParams,
  TPurchaseBundleIdParams,
  TReplicateUnlockedContentParams,
  TStartPaymentParams,
  TStartPaymentResult,
} from './locks.types';

// v1 payment locks must hold exactly one criterion, referenced once by the lock logic.
const CRITERION_ID = 'criterion-1';
// Only asset the Lock Server's v1 payment verifier takes.
const PAYMENT_ASSET = 'BTC';
const CREDENTIAL_TTL_SECONDS = 900;

/**
 * Application layer for the Lock Server: auth (mirrors `AuthApplication`), publishing locked content
 * (creator), and reading a lock's public `lock.json` (reader).
 *
 * The auth methods delegate straight to the service — their real orchestration (session persistence,
 * store writes) lives in `LocksController`, since only controllers manage stores (ADR 0004).
 */
export class LocksApplication {
  private constructor() {} // Prevent instantiation

  static generateConnectUrl(params: TGenerateConnectUrlParams): Promise<string> {
    return LocksService.generateConnectUrl(params);
  }

  static generatePaykitSetupUrl(params: TGeneratePaykitSetupUrlParams): string {
    return LocksService.generatePaykitSetupUrl(params);
  }

  /** Whether the Paykit payout account of the current Locks session's creator is set up. */
  static fetchPaykitSetupStatus(): Promise<TPaykitSetupStatus> {
    return LocksService.lookupPaykitSetupStatus();
  }

  /** Whether the Lock Server at `origin` is ready to serve — gates the auth flow before the iframe. */
  static isServerReady(origin: string): Promise<boolean> {
    return LocksService.isServerReady(origin);
  }

  /**
   * Reads `/priv/social/purchases/<lockId>.json` from the reader's own homeserver and returns the
   * bundle id inside, or null when the file does not exist. A file that exists but cannot be parsed
   * throws: a purchase was made and its id is lost, so the caller must show an error instead of
   * offering to pay again with a fresh id, which could charge the reader twice.
   */
  static async fetchPurchaseBundleId({ lockUrl, readerPubky }: TPurchaseBundleIdParams): Promise<string | null> {
    const lockId = this.requireLockId(lockUrl, 'fetchPurchaseBundleId');
    const stored = await HomeserverService.getBytesIfExists(GuardedContentParser.purchaseUrl(readerPubky, lockId));
    if (!stored) return null;

    const bundleId = GuardedContentParser.parsePurchaseFile(stored.bytes);
    if (!bundleId) {
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'purchase bundle id file is unreadable', {
        service: ErrorService.Locks,
        operation: 'fetchPurchaseBundleId',
        context: { lockId },
      });
    }
    return bundleId;
  }

  /** Whether the reader's wallet has published a Paykit receiver. */
  static hasPaykitReceiver(readerPubky: string): Promise<boolean> {
    return LocksService.hasPaykitReceiver(readerPubky);
  }

  /**
   * Starts (or restarts) a payment: reuses the bundle id saved on the reader's homeserver, or mints
   * and saves a fresh one when there is none or the saved one is `rejectBundleId`. Then submits the
   * proof to the Lock Server, which creates the verification task (or returns the existing one on a
   * replay of the same id) and has Paykit deliver the payment request to the reader's wallet.
   */
  static async startPayment({
    lockFile,
    lockUrl,
    readerPubky,
    rejectBundleId,
  }: TStartPaymentParams): Promise<TStartPaymentResult> {
    const lockId = this.requireLockId(lockUrl, 'startPayment');
    const run = async (): Promise<TStartPaymentResult> => {
      let bundleId = await this.fetchPurchaseBundleId({ lockUrl, readerPubky });
      if (!bundleId || bundleId === rejectBundleId) {
        bundleId = await LocksService.generateBundleId();
        // Saved BEFORE the proof is submitted: a submission that succeeds while the file is missing
        // leaves a purchase nobody can reach.
        await HomeserverService.putBlob({
          url: GuardedContentParser.purchaseUrl(readerPubky, lockId),
          blob: new TextEncoder().encode(GuardedContentParser.buildPurchaseFile(bundleId)),
        });
      }
      const bundle = LockProofBundler.buildPayment(lockFile, lockUrl, bundleId, readerPubky);
      const task = await LocksService.submitProof(bundle);
      return { bundleId, status: task.status };
    };
    // Every browser Next.js supports has Web Locks; only an insecure context (plain-http dev) lacks it.
    const lockManager = typeof navigator === 'undefined' ? undefined : navigator.locks;
    if (!lockManager) return run();

    try {
      return await lockManager.request(`locks-pay:${readerPubky}:${lockId}`, run);
    } catch (error) {
      // `request` itself can reject with a DOMException that no Err factory has reported.
      throw toAppError(error, ErrorService.Locks, 'LocksApplication.startPayment');
    }
  }

  /**
   * One read of the Paykit link for a submitted payment. Read-only: it creates no invoice and never
   * advances the handshake, and it needs the task the proof submission created.
   */
  static fetchPaykitConnectionState({ lockFile, bundleId }: TPaymentBundleParams): Promise<TPaykitConnectionState> {
    return LocksService.lookupPaykitConnectionState(lockFile.creator, bundleId);
  }

  /**
   * One read of where the Lock Server's payment verification stands for a saved bundle id, or null
   * when the server has no task for it (the submission never reached it).
   */
  static async fetchPaymentStatus({ lockFile, bundleId }: TPaymentBundleParams): Promise<TVerificationStatus | null> {
    const task = await LocksService.lookupVerificationTask(lockFile.creator, bundleId);
    return task?.status ?? null;
  }

  /**
   * Lock ids the reader has started a payment for, from `/priv/social/purchases/`. The bundle id
   * file is written before the proof is submitted, so pending and failed purchases are included.
   * One listing answers it for every lock post on screen, so the feed never asks per post.
   */
  static async fetchPurchasedLockIds({ readerPubky }: TFetchUnlockedListParams): Promise<string[]> {
    const files = await HomeserverService.listAll({
      baseDirectory: GuardedContentParser.purchasesRootUrl(readerPubky),
    });
    return GuardedContentParser.purchasedLockIds(files);
  }

  /**
   * Gets an access credential for a paid bundle id from the Lock Server, then downloads the post
   * and attachments with it. Replication stays with the caller, which owns the one path that
   * writes it.
   *
   * The credential is short-lived and never stored, so an expired one is not a state to detect:
   * this mints a new one on every call, and a mid-read expiry is answered by calling again.
   */
  static async fetchPaidContent({ lockFile, bundleId }: TPaymentBundleParams): Promise<TUnlockedContent> {
    const { credential } = await LocksService.issueAccessCredential(lockFile.creator, bundleId);
    return this.fetchUnlockedContent({ lockFile, credential });
  }

  /**
   * `fetchPaidContent` for a caller that knows neither the bundle id nor whether the payment went
   * through: looks up the saved id, checks the verification status, and downloads only when it is
   * `completed`; null otherwise. Read-only; never mints an id or submits a proof.
   */
  static async fetchPaidContentIfCompleted({
    lockFile,
    lockUrl,
    readerPubky,
  }: TPaymentLockParams): Promise<TUnlockedContent | null> {
    const bundleId = await this.fetchPurchaseBundleId({ lockUrl, readerPubky });
    if (!bundleId) return null;
    const status = await this.fetchPaymentStatus({ lockFile, bundleId });
    if (status !== 'completed') return null;
    return this.fetchPaidContent({ lockFile, bundleId });
  }

  /** `pubky://…/<lock_id>.json` → `<lock_id>`, or a typed error for a malformed lock URL. */
  private static requireLockId(lockUrl: string, operation: string): string {
    const lockId = LockContentParser.lockIdFromUrl(lockUrl);
    if (!lockId) {
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'lock URL has no lock id', {
        service: ErrorService.Locks,
        operation,
        context: { lockUrl },
      });
    }
    return lockId;
  }

  /** Reads the guarded post + its attachments with the access credential. Throws when the post is unparseable. */
  static async fetchUnlockedContent({ lockFile, credential }: TFetchUnlockedContentParams): Promise<TUnlockedContent> {
    const primaryPath = lockFile.primary_resource?.path;
    const readPath = primaryPath ? GuardedContentParser.toReadPath(primaryPath) : null;
    if (!readPath) {
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'lock file has no readable primary resource', {
        service: ErrorService.Locks,
        operation: 'fetchUnlockedContent',
        context: { primaryPath },
      });
    }

    const primaryBytes = await LocksService.proxyReadGuardedResource(credential, readPath);
    const post = GuardedContentParser.parsePost(primaryBytes);
    if (!post) {
      // Unlock succeeded but the primary resource isn't a parseable post — a permanent data error, so
      // report it (like a dropped attachment) instead of a silent null the caller can't distinguish.
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'unlocked guarded post is not parseable', {
        service: ErrorService.Locks,
        operation: 'fetchUnlockedContent',
        context: { readPath },
      });
    }

    // Reader path: proxy-read each attachment through the Lock Server with the access credential.
    const readBytes = (path: string) => this.proxyReadAttachment(credential, path);
    const attachments = await this.readAttachments(lockFile, post.attachments ?? [], 'fetchUnlockedContent', readBytes);
    return { post, attachments };
  }

  /** Proxy-reads one attachment: strips the guarded prefix to the relative path the Lock Server expects. */
  private static proxyReadAttachment(credential: string, path: string): Promise<Uint8Array> {
    const readPath = GuardedContentParser.toReadPath(path);
    if (!readPath) {
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'attachment path is outside the guarded namespace', {
        service: ErrorService.Locks,
        operation: 'fetchUnlockedContent',
        context: { path },
      });
    }
    return LocksService.proxyReadGuardedResource(credential, readPath);
  }

  /**
   * Pairs each attachment's bytes with its content type (from the lock file), reading the bytes via the
   * caller's `readBytes` (reader = proxy-read with a credential; creator = direct read of their own HS).
   * A validation error (permanent data fault) drops only that attachment; any other failure rejects
   * the whole read so no caller persists a partial result — see the catch below.
   *
   * TODO:[Locks] #2374 — a permanently dropped attachment still lets the marker land, so the lock
   * reads as fully unlocked and the attachment is unrecoverable.
   */
  private static async readAttachments(
    lockFile: LockFile,
    uris: string[],
    operation: string,
    readBytes: (path: string, uri: string) => Promise<Uint8Array>,
  ): Promise<TUnlockedAttachment[]> {
    const reads = await Promise.all(
      uris.map(async (uri, slot) => {
        try {
          const path = GuardedContentParser.attachmentUriToPath(uri);
          const contentType = lockFile.secondary_resources?.[path]?.content_type;
          // No descriptor = a permanent data-integrity error (the bytes live on a HS with no content
          // type, so they can never render). Report to Sentry, then drop this one attachment.
          if (!contentType) {
            throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'guarded attachment has no content descriptor', {
              service: ErrorService.Locks,
              operation,
              context: { path },
            });
          }
          return { id: path.slice(path.lastIndexOf('/') + 1), contentType, bytes: await readBytes(path, uri), slot };
        } catch (error) {
          // Validation = permanent data error (bad uri / no descriptor / outside namespace), already
          // reported — retrying can't fix it, so drop this attachment and let the rest render.
          if (isAppError(error) && isValidationError(error)) return null;
          // Anything else is a transient `readBytes` failure (the proxy-read / homeserver GET —
          // network, 5xx): rethrow so the fetch fails before `replicateUnlockedContent` writes the
          // `post.json` marker, keeping the unlock retryable.
          throw error;
        }
      }),
    );
    return reads.filter((attachment): attachment is TUnlockedAttachment => attachment !== null);
  }

  /**
   * Copies unlocked content into the reader's own `/priv/social/unlocked/<lockId>/`, so re-reading it
   * later needs no credential and survives the creator revoking access.
   *
   * Attachments upload first: `post.json` is the completion marker (§7 reads it to decide whether a
   * lock is already unlocked), so it must land only once everything it references is stored. A partial
   * run therefore leaves no marker and is simply retried on the next unlock.
   */
  static async replicateUnlockedContent({
    lockUrl,
    readerPubky,
    content,
    announcementUri,
  }: TReplicateUnlockedContentParams): Promise<void> {
    const lockId = this.requireLockId(lockUrl, 'replicateUnlockedContent');

    for (const attachment of content.attachments) {
      await HomeserverService.putBlob({
        url: GuardedContentParser.unlockedUrl(readerPubky, lockId, attachment.id),
        blob: attachment.bytes,
      });
    }

    await HomeserverService.putBlob({
      url: GuardedContentParser.unlockedPostUrl(readerPubky, lockId),
      blob: new TextEncoder().encode(
        GuardedContentParser.buildUnlockedPost(content.post, readerPubky, lockId, content.attachments, announcementUri),
      ),
    });
  }

  /**
   * Lists the reader's unlocked content from `/priv/social/unlocked/`, newest unlock first.
   * Only locks whose `post.json` marker exists count — a partial replica has none. A corrupt or
   * concurrently-deleted marker drops that one item so the rest still renders; any other failure
   * rejects the whole list so the caller can retry.
   *
   * TODO:[Locks] #2296 — uncached, so every profile visit re-lists the root and re-GETs each marker.
   * The reader's unlocked content moves to IndexedDB there, which replaces this with a local read.
   */
  static async fetchUnlockedList({ readerPubky }: TFetchUnlockedListParams): Promise<TUnlockedListItem[]> {
    const files = await HomeserverService.listAll({
      baseDirectory: GuardedContentParser.unlockedRootUrl(readerPubky),
    });

    const items = await Promise.all(
      GuardedContentParser.completedLockIds(files).map(async (lockId) => {
        try {
          const replicatedPost = await this.readReplicatedMarker(readerPubky, lockId, 'fetchUnlockedList');
          if (!replicatedPost) return null;
          // A marker from before the announcement was recorded, or an unparseable URI, still lists —
          // it just renders without its announcement post.
          // Spread rather than an `undefined` value: the key stays absent, which is what the optional
          // field and the narrowing filter below both expect.
          const announcementPostId = replicatedPost.post.announcement
            ? buildCompositeIdFromPubkyUri({ uri: replicatedPost.post.announcement, domain: CompositeIdDomain.POSTS })
            : null;
          return { lockId, ...replicatedPost, ...(announcementPostId ? { announcementPostId } : {}) };
        } catch (error) {
          // Validation = corrupt marker, already reported — drop this item only.
          // So user will see validated locks but not invalid ones.
          if (isAppError(error) && isValidationError(error)) return null;
          throw error;
        }
      }),
    );

    return items.filter((item): item is TUnlockedListItem => item !== null).sort((a, b) => b.unlockedAt - a.unlockedAt);
  }

  /**
   * Reads + parses a lock's `post.json` unlock marker. Null when absent — never unlocked, partial
   * replica, or deleted meanwhile; `getBytesIfExists` swallows the 404 quietly (no error log).
   * A marker present but corrupt (a 200 with unparseable bytes) is a data error, not "not unlocked" —
   * reported via throw instead of a silent null.
   */
  private static async readReplicatedMarker(
    readerPubky: string,
    lockId: string,
    operation: string,
  ): Promise<{ post: ReplicatedPost; unlockedAt: number } | null> {
    const marker = await HomeserverService.getBytesIfExists(GuardedContentParser.unlockedPostUrl(readerPubky, lockId));
    if (!marker) return null;

    const post = GuardedContentParser.parseReplicatedPost(marker.bytes);
    if (!post) {
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'replicated post is not parseable', {
        service: ErrorService.Locks,
        operation,
        context: { lockId },
      });
    }
    // The marker is written once, when the unlock completes, so its server-side write time IS the
    // unlock time. 0 when the header is missing, which sorts the item oldest.
    return { post, unlockedAt: marker.modifiedAt ?? 0 };
  }

  /** Already unlocked → load from reader's HS `/priv`. Null if no `post.json` (never unlocked or partial). */
  static async fetchReplicatedContent({
    lockUrl,
    readerPubky,
  }: TFetchReplicatedContentParams): Promise<TUnlockedContent | null> {
    const lockId = LockContentParser.lockIdFromUrl(lockUrl);
    if (!lockId) return null;

    const replicatedPost = await this.readReplicatedMarker(readerPubky, lockId, 'fetchReplicatedContent');
    if (!replicatedPost) return null;

    const { post } = replicatedPost;
    const refs = post.attachments ?? [];
    return {
      post: { content: post.content, kind: post.kind, attachments: refs.map((ref) => ref.url) },
      attachments: await this.fetchReplicatedAttachments({ post }),
    };
  }

  /**
   * Loads the bytes behind a replicated marker's attachments. Split from `fetchReplicatedContent` so
   * the unlocked list, which already holds the markers, can pull media without re-reading them.
   *
   * Missing file (404) → drop it, show the rest. Anything else → throw, so a brief outage does not
   * look like lost media. Same rule as `readAttachments`.
   */
  static async fetchReplicatedAttachments({ post }: TFetchReplicatedAttachmentsParams): Promise<TUnlockedAttachment[]> {
    const reads = await Promise.all(
      (post.attachments ?? []).map(async ({ url, content_type, slot }, index) => {
        try {
          return {
            id: url.slice(url.lastIndexOf('/') + 1),
            contentType: content_type,
            bytes: await HomeserverService.getBytes(url),
            slot: slot ?? index,
          };
        } catch (error) {
          // 404 = the replica lost this file; `getBytes` already reported it.
          if (isAppError(error) && isNotFound(error)) return null;
          throw error;
        }
      }),
    );
    return reads.filter((attachment): attachment is TUnlockedAttachment => attachment !== null);
  }

  /**
   * Creator reads their OWN locked content straight from their homeserver
   * (`/priv/app.locks/content/`) — no unlock, no credential, no replication.
   * Only valid when the lock owner is the signed-in account (a == b); the caller
   * verifies that before calling.
   */
  static async fetchOwnContent({ lockFile }: TFetchOwnContentParams): Promise<TUnlockedContent> {
    const primaryPath = lockFile.primary_resource?.path;
    if (!primaryPath) {
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'lock file has no readable primary resource', {
        service: ErrorService.Locks,
        operation: 'fetchOwnContent',
        context: { primaryPath },
      });
    }

    const owner = stripPubkyPrefix(lockFile.creator);
    const post = GuardedContentParser.parsePost(await HomeserverService.getBytes(`pubky://${owner}${primaryPath}`));
    if (!post) {
      // 200 but unparseable — the creator's own guarded original is corrupt. Report, don't return null.
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'own guarded post is not parseable', {
        service: ErrorService.Locks,
        operation: 'fetchOwnContent',
        context: { owner, primaryPath },
      });
    }

    // Creator path: the guarded original lives on their own HS, so read each attachment URI directly.
    const attachments = await this.readAttachments(lockFile, post.attachments ?? [], 'fetchOwnContent', (_path, uri) =>
      HomeserverService.getBytes(uri),
    );
    return { post, attachments };
  }

  static exchangeSessionCode(params: TExchangeSessionCodeParams): Promise<TLocksSessionResult> {
    return LocksService.exchangeSessionCode(params);
  }

  static restoreSession(): Promise<LocksSdkSession> {
    return LocksService.restoreSession();
  }

  static signout(): Promise<void> {
    return LocksService.signout();
  }

  static setLockServiceConfig(): Promise<void> {
    return LocksService.setLockServiceConfig();
  }

  /**
   * Publishes one content lock: uploads every attachment, builds the post from their paths, uploads
   * that too, then bundles the lot into one lock. The post becomes the lock's primary resource, the
   * attachments its secondary resources.
   *
   * Attachments go first because a resource's path only exists once its bytes are uploaded, and the
   * post has to reference them. Uploads are sequential.
   *
   * TODO:[Locks] #2039 — a failure part-way leaves the already-uploaded resources orphaned on the
   * server. See the note on `LocksService.createContentLock` for the cleanup rules.
   *
   * The composer checks the file count and size against the Lock Server defaults first
   * (`LOCK_ATTACHMENT_*` in `@/config/posts`); the server stays the authority.
   */
  static async createLockContent({
    attachments = [],
    buildPost,
    lockConfig,
  }: TCreateLockContentParams): Promise<TCreateContentLockResult> {
    // The guarded bytes land on the Lock-Server-authenticated account, which may differ from the
    // pubky.app user. Capture that owner from the upload response so `buildPost` can reference the
    // attachments by their real host. With no attachments the owner stays undefined and no URIs are built.
    let owner: string | undefined;
    const attachmentResources: TGuardedResource[] = [];
    for (const file of attachments) {
      const uploaded = await this.upload(file);
      owner = uploaded.creator;
      attachmentResources.push(uploaded.resource);
    }
    const post = await this.upload(buildPost(attachmentResources, owner));

    // The payout recipient has to equal the lock's creator, and the upload response names that
    // account — so the criterion can only be built here, once the primary resource is up.
    const criterion = {
      criterion_id: CRITERION_ID,
      verifier_type: VerifierType.PAYMENT,
      params: { recipient_pubky: post.creator, amount: lockConfig.amountSats, asset: PAYMENT_ASSET },
    };

    return LocksService.createContentLock({
      primaryResource: post.resource,
      secondaryResources: attachmentResources,
      criteria: [criterion],
      lockLogic: { type: 'all', criteria: [CRITERION_ID] },
      accessPolicy: { requested_credential_ttl_seconds: CREDENTIAL_TTL_SECONDS },
    });
  }

  /**
   * Uploads one file under a freshly minted path and returns its descriptor.
   *
   * The path is a random id, never the original filename: two attachments named `cover.png` would
   * resolve to the same path and the second upload would silently overwrite the first. This mirrors
   * normal posts, where the blob path comes from a spec-generated id and the filename survives only
   * as display metadata on `PubkyAppFile`.
   */
  private static upload({ contentType, bytes }: TLockContentFile): Promise<TRegisterGuardedResourceResult> {
    return LocksService.registerGuardedResource({
      path: crypto.randomUUID(),
      contentType,
      bytes,
    });
  }

  /** Reads the creator's public `lock.json`. Throws for a malformed `lock` URL. */
  static async fetchLockFile({ lockUrl }: TFetchLockFileParams): Promise<LockFile | null> {
    if (!LockContentParser.isValidLockUrl(lockUrl)) {
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'post lock URL is not a valid pubky homeserver URL', {
        service: ErrorService.Locks,
        operation: 'fetchLockFile',
        context: { lockUrl },
      });
    }

    return (await LocksService.readContentLock(lockUrl)) as LockFile;
  }
}
